import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { PopupService } from '@app/shared/popup';
import { filter, map, Subscription, take, tap } from 'rxjs';

import { Router } from '@angular/router';
import { ContentPathService } from '@app/content';
import { StorageService } from '@app/core/service';
import { DropdownOpenCloseService } from '@app/tw-shared/directives/dropdown-open-close/dropdown-open-close.service';
import { UserService } from '@app/user';
import { AdminSettingsNavItemsService } from '../modules/nav-item-admin/services/admin-settings-nav-items.service';
import { setGrow } from '../modules/search/animations/grow.animation';
import { HeaderMenuService } from '../service/header-menu/header-menu.service';
import { HeaderService } from '../service/header/header.service';
import { LogoutPopupComponent } from './logout/logout.component';
import { LearnNavItemsService, MobileHeaderNavItemsService, SettingsNavItemsService } from './services';
import { AccessibilityService } from '@app/tw-shared/services';
import { menuItemsFilter } from './utils';
import { WindowService } from '@app/shared/services';
import { NotificationsRedbubbleService } from '@app/notifications';

@Component({
    selector: 'app-header-menu',
    templateUrl: './menu.component.html',
    styleUrls: ['./menu.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [setGrow()],
    standalone: false,
})
export class MenuComponent implements OnInit, OnDestroy {
    @Input() open = false;
    private readonly headerService = inject(HeaderService);
    private readonly headerMenuService = inject(HeaderMenuService);
    private readonly userService = inject(UserService);
    private readonly router = inject(Router);
    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly contentPathService = inject(ContentPathService);
    private readonly storageService = inject(StorageService);
    private readonly popupService = inject(PopupService);
    private readonly dropdownOpenCloseService = inject(DropdownOpenCloseService);
    private readonly adminSettingsNavItemsService = inject(AdminSettingsNavItemsService);
    private readonly learnNavItemsService = inject(LearnNavItemsService);
    private readonly settingsNavItemsService = inject(SettingsNavItemsService);
    private readonly mobileHeaderNavItemsService = inject(MobileHeaderNavItemsService);
    private readonly accessibilityService = inject(AccessibilityService);
    private readonly windowService = inject(WindowService);
    private readonly notificationsRedbubbleService = inject(NotificationsRedbubbleService);

    readonly canAccessNotifications$ = this.userService.access$.pipe(map((access) => access.includes('notifications')));

    readonly isVeryLargeFontSize$ = this.accessibilityService.isVeryLargeFontSize$;

    private subscriptions: Subscription = new Subscription();

    readonly announcementsLink = 'https://releasenotes.socialtalent.com/releases';

    readonly adminItems$ = this.adminSettingsNavItemsService.items$.pipe(
        menuItemsFilter({ windowService: this.windowService, isVeryLargeFontSize$: this.isVeryLargeFontSize$ }),
    );
    readonly headerItems$ = this.headerService.items$.pipe(
        menuItemsFilter({ windowService: this.windowService, isVeryLargeFontSize$: this.isVeryLargeFontSize$ }),
    );
    readonly learnItems$ = this.learnNavItemsService.learnItems$;
    readonly settingsItems$ = this.settingsNavItemsService.settingsItems$;
    readonly mobileHeaderItems$ = this.mobileHeaderNavItemsService.mobileHeaderItems$;

    readonly belowXl$ = this.windowService.belowXL$;

    get user() {
        return this.userService.user;
    }

    get canLearn(): boolean {
        return this.userService.access.includes('content');
    }

    get challengesCount() {
        return this.contentPathService.missions.filter(
            (mission) => mission.stats.challenge?.available && mission.stats.challenge?.ready && !mission.stats.challenge?.completed,
        ).length;
    }

    get hasSelfRegistration() {
        return this.user.options.attention.setup.selfRegistration.enabled;
    }

    get dropdownItems() {
        return this.headerMenuService.items;
    }

    get hasTrial() {
        return this.userService.user.company.trial;
    }

    get hasManyCompanies(): boolean {
        return this.user.hasManyCompanies;
    }

    get notificationCount(): number {
        return this.notificationsRedbubbleService.count;
    }

    ngOnInit() {
        this.subscriptions.add(this.notificationsRedbubbleService.updates.pipe(tap(() => this.changeDetectorRef.detectChanges())).subscribe());
        this.subscriptions.add(this.contentPathService.updates.pipe(tap(() => this.changeDetectorRef.detectChanges())).subscribe());
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }

    close() {
        this.dropdownOpenCloseService.openEvent$.next(false);
    }

    logoutClick() {
        if (this.storageService.read('logoutDoNotShowMessage') === true) {
            this.logout();
            return;
        }

        this.openLogoutPopup();
        this.listenToLogoutPopupCloseEvent();
    }

    goToCompanySelection() {
        const loginConfiguration = this.userService.user?.company?.loginConfiguration;
        const configurationName = loginConfiguration && loginConfiguration.enabled && loginConfiguration.name ? loginConfiguration.name : null;
        this.router.navigate(configurationName ? [`/select-company/b/${configurationName}`] : ['/select-company'], {
            queryParams: { goBackUrl: this.router.url },
        });
    }

    private openLogoutPopup() {
        this.popupService.open(LogoutPopupComponent);
    }

    private listenToLogoutPopupCloseEvent() {
        this.popupService.closingSubject
            .pipe(
                take(1),
                filter((data: 'logout' | 'close') => data === 'logout'),
                tap(() => this.logout()),
            )
            .subscribe();
    }

    private logout() {
        this.router.navigate(['/logout'], { skipLocationChange: true });
    }
}
